import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Platform,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { simulateStripeCheckout, formatCurrency } from '../services/paymentGateway';
import { getTournamentById, addTournamentParticipant } from '../services/database';

const TournamentDetailScreen = ({ navigation, route }) => {
  const { tournamentId } = route.params;
  const { user, appUser } = useAuth();
  const { showToast } = useToast();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [profile, setProfile] = useState(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      fetchTournament();
    }
  }, [profile, tournamentId]);

  const fetchProfile = async () => {
    if (appUser) {
      setProfile(appUser);
    }
  };

  const handleJoinTournament = async () => {
    if (!profile) return;
    if (!['DRAFT', 'REGISTRATION'].includes(tournament.status)) {
      showToast('Registration is closed for this event', 'info');
      return;
    }

    try {
      setJoining(true);
      let paymentMeta = {};
      if (tournament.isPaid) {
        if (!tournament.entryFee) {
          showToast('Entry fee missing. Contact the club.', 'error');
          setJoining(false);
          return;
        }
        const paymentResult = await simulateStripeCheckout({
          amount: tournament.entryFee,
          description: tournament.name,
        });
        paymentMeta = {
          hasPaid: true,
          paymentStatus: 'PAID',
          paymentIntentId: paymentResult.paymentIntentId,
          amountPaid: paymentResult.amountPaid,
        };
      }

      await addTournamentParticipant({
        tournament_id: tournamentId,
        user_id: profile.id,
        guest_name:
          profile.accountType === 'CLUB'
            ? profile.clubName || 'Club'
            : profile.username || 'Player',
        score: 0,
        rating_snapshot: profile.rating || 1200,
        ...(paymentMeta.hasPaid !== undefined ? {
          has_paid: paymentMeta.hasPaid,
          payment_status: paymentMeta.paymentStatus,
          payment_intent_id: paymentMeta.paymentIntentId,
          amount_paid: paymentMeta.amountPaid,
        } : {}),
      });
      showToast('Registered for tournament', 'success');
      fetchTournament();
    } catch (error) {
      if (error?.message === 'PAYMENT_CANCELLED') {
        showToast('Payment cancelled', 'info');
      } else {
      console.error('Error joining tournament:', error);
        showToast('Unable to join tournament', 'error');
      }
    } finally {
      setJoining(false);
    }
  };

  const fetchTournament = async () => {
    try {
      setLoading(true);
      const raw = await getTournamentById(tournamentId);
      const mapped = {
        ...raw,
        totalRounds: raw.total_rounds,
        currentRound: raw.current_round,
        allowPhones: raw.allow_phones,
        startTime: raw.start_time,
        projectorCode: raw.projector_code,
        isPaid: raw.is_paid,
        entryFee: raw.entry_fee,
        formatConfig: raw.format_config,
        clubId: raw.club_id,
        club: raw.club ? { ...raw.club, clubName: raw.club.club_name } : null,
        participants: {
          items: (raw.participants || []).map(p => ({
            ...p,
            userId: p.user_id,
            guestName: p.guest_name,
            seedNumber: p.seed_number,
            currentBoard: p.current_board,
            hasPaid: p.has_paid,
            paymentStatus: p.payment_status,
            amountPaid: p.amount_paid,
            ratingSnapshot: p.rating_snapshot,
          })),
        },
        standings: { items: [] },
      };
      setTournament(mapped);
    } catch (error) {
      console.error('Error loading tournament:', error);
      showToast('Failed to load tournament details', 'error');
      navigation.goBack();
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchTournament();
  };

  if (loading || !tournament) {
    return (
      <View style={styles.centered}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  const isClubOwner =
    profile?.accountType === 'CLUB' && profile?.id === tournament.clubId;

  const standings =
    tournament?.standings?.items
      ?.filter(Boolean)
      ?.sort((a, b) => (a.rank || 999) - (b.rank || 999)) || [];

  const participants = tournament?.participants?.items || [];

  const derivedStandings = standings.length
    ? standings
    : participants
        .map((participant) => ({
          id: participant.id,
          rank: 0,
          score: participant.score || 0,
          tieBreak1: participant.seedNumber ? 1000 - participant.seedNumber : 0,
          participant,
        }))
        .sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          return (a.tieBreak1 || 0) - (b.tieBreak1 || 0);
        })
        .map((item, index) => ({ ...item, rank: index + 1 }));
  const formatSettings = useMemo(() => {
    if (!tournament?.formatConfig) return {};
    try {
      return JSON.parse(tournament.formatConfig) || {};
    } catch (error) {
      console.warn('Invalid tournament formatConfig', error);
      return {};
    }
  }, [tournament]);
  const announcements = formatSettings.announcements || [];
  const myParticipant = participants.find((p) => p?.userId && p.userId === profile?.id);
  const isParticipant = !!myParticipant;
  const canJoin =
    profile?.accountType === 'PLAYER' &&
    !isParticipant &&
    ['DRAFT', 'REGISTRATION'].includes(tournament.status);
  const playerPaymentStatus = myParticipant?.paymentStatus || 'PENDING';
  const formatAnnouncementTime = (iso) => {
    try {
      const date = new Date(iso);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <ScrollView
      style={styles.container}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
    >
      <View style={styles.hero}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name="arrow-back" size={24} color="white" />
        </TouchableOpacity>
        <Text style={styles.heroTitle}>{tournament.name}</Text>
        <Text style={styles.heroSubtitle}>
          {tournament.type.replace('_', ' ')} • {tournament.totalRounds || '?'} rounds •{' '}
          {(() => {
            try {
              const config = JSON.parse(tournament.formatConfig || '{}');
              return config.timeControl || 'time control TBD';
            } catch {
              return 'time control TBD';
            }
          })()}
        </Text>
        <View style={[styles.statusBadge, styles[`status${tournament.status}`]]}>
          <Text style={styles.statusText}>{tournament.status.replace('_', ' ')}</Text>
        </View>
      </View>

      {(announcements.length > 0 || isClubOwner) && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Announcements</Text>
          {announcements.length === 0 ? (
            <Text style={styles.helperText}>No announcements yet.</Text>
          ) : (
            announcements.slice(0, 5).map((announcement) => (
              <View key={announcement.id} style={styles.announcementRow}>
                <Icon name="campaign" size={20} color="#1976D2" />
                <View style={{ marginLeft: 12, flex: 1 }}>
                  <Text style={styles.playerName}>{announcement.message}</Text>
                  <Text style={styles.playerSubtext}>{formatAnnouncementTime(announcement.createdAt)}</Text>
                </View>
              </View>
            ))
          )}
        </View>
      )}

      {canJoin && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Join this tournament</Text>
          <Text style={styles.description}>
            Tap below to reserve your seat. Entry fee:{' '}
            {tournament.isPaid ? formatCurrency(tournament.entryFee || 0) : 'Free'}.
          </Text>
          <TouchableOpacity
            style={[styles.primaryButton, joining && styles.primaryButtonDisabled]}
            onPress={handleJoinTournament}
            disabled={joining}
          >
            {joining ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <>
                <Icon name="person-add" size={20} color="#fff" />
                <Text style={styles.primaryButtonText}>
                  {tournament.isPaid
                    ? `Pay ${formatCurrency(tournament.entryFee || 0)}`
                    : 'Join Tournament'}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Overview</Text>
        <Text style={styles.description}>{tournament.description || 'No description provided.'}</Text>
        <View style={styles.overviewGrid}>
          <View style={styles.overviewItem}>
            <Text style={styles.overviewLabel}>Rounds</Text>
            <Text style={styles.overviewValue}>
              {tournament.currentRound || 0}/{tournament.totalRounds || '?'}
            </Text>
          </View>
          <View style={styles.overviewItem}>
            <Text style={styles.overviewLabel}>Players</Text>
            <Text style={styles.overviewValue}>{participants.length}</Text>
          </View>
          <View style={styles.overviewItem}>
            <Text style={styles.overviewLabel}>Devices</Text>
            <Text style={styles.overviewValue}>
              {tournament.allowPhones ? 'Allowed' : 'Disabled'}
            </Text>
          </View>
          <View style={styles.overviewItem}>
            <Text style={styles.overviewLabel}>Entry Fee</Text>
            <Text style={styles.overviewValue}>
              {tournament.isPaid ? formatCurrency(tournament.entryFee || 0) : 'Free'}
            </Text>
          </View>
        </View>
      </View>

      {isClubOwner && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Director Tools</Text>
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={() => navigation.navigate('TournamentControl', { tournamentId })}
          >
            <Icon name="settings" size={20} color="white" />
            <Text style={styles.primaryButtonText}>Open Control Mode</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => navigation.navigate('TournamentProjector', { tournament })}
          >
            <Icon name="tv" size={20} color="#333" />
            <Text style={styles.secondaryButtonText}>Projector Mode</Text>
          </TouchableOpacity>
        </View>
      )}

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Standings</Text>
          <TouchableOpacity onPress={() => showToast('Detailed standings coming soon', 'info')}>
            <Text style={styles.link}>View Details</Text>
          </TouchableOpacity>
        </View>
        {derivedStandings.length === 0 ? (
          <View style={styles.emptyState}>
            <Icon name="leaderboard" size={48} color="#ccc" />
            <Text style={styles.emptyTitle}>No standings yet</Text>
            <Text style={styles.emptySubtitle}>
              Pairings and standings update as rounds are completed.
            </Text>
          </View>
        ) : (
          derivedStandings.slice(0, 6).map((standing) => (
            <View key={standing.id} style={styles.standingRow}>
              <Text style={styles.rankBadge}>{standing.rank || '-'}</Text>
              <View style={{ flex: 1 }}>
                <Text style={styles.playerName}>
                  {standing.participant?.guestName ||
                    `Player ${standing.participant?.id.substring(0, 5)}`}
                </Text>
                <Text style={styles.playerSubtext}>
                  {standing.score?.toFixed(1) || 0} pts • TB1 {standing.tieBreak1 || 0}
                </Text>
              </View>
            </View>
          ))
        )}
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Participants</Text>
          <TouchableOpacity
            onPress={() => navigation.navigate('TournamentPlayer', { tournamentId })}
          >
            <Text style={styles.link}>Player View</Text>
          </TouchableOpacity>
        </View>
        {participants.length === 0 ? (
          <View style={styles.emptyState}>
            <Icon name="people" size={48} color="#ccc" />
            <Text style={styles.emptyTitle}>No participants yet</Text>
          </View>
        ) : (
          participants.slice(0, 6).map((participant) => (
            <View key={participant.id} style={styles.participantRow}>
              <View style={styles.avatar}>
                <Text style={styles.avatarText}>
                  {(participant.guestName || 'P')[0].toUpperCase()}
                </Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.playerName}>
                  {participant.guestName || `Player ${participant.id.substring(0, 5)}`}
                </Text>
                <Text style={styles.playerSubtext}>
                  Score {participant.score?.toFixed(1) || '0.0'} • Board{' '}
                  {participant.currentBoard || '-'}
                </Text>
              </View>
            </View>
          ))
        )}
        {isParticipant && tournament.isPaid && (
          <Text style={styles.helperText}>
            Payment status: {myParticipant?.hasPaid ? 'Paid ✅' : 'Pending payment'}
          </Text>
        )}
      </View>

      <View style={{ height: 40 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  hero: {
    backgroundColor: '#0D1117',
    paddingTop: Platform.OS === 'ios' ? 60 : 30,
    paddingBottom: 24,
    paddingHorizontal: 20,
  },
  backButton: {
    marginBottom: 20,
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: 'white',
  },
  heroSubtitle: {
    fontSize: 15,
    color: '#9BA4B5',
    marginTop: 4,
  },
  statusBadge: {
    alignSelf: 'flex-start',
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  statusText: {
    color: 'white',
    fontWeight: '600',
  },
  statusIN_PROGRESS: {
    backgroundColor: '#2E7D32',
  },
  statusDRAFT: {
    backgroundColor: '#9E9E9E',
  },
  statusUPCOMING: {
    backgroundColor: '#0277BD',
  },
  statusCOMPLETED: {
    backgroundColor: '#6D4C41',
  },
  statusCANCELLED: {
    backgroundColor: '#C62828',
  },
  section: {
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: Platform.OS === 'ios' ? 0.08 : 0.2,
    shadowRadius: 10,
    elevation: 3,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  description: {
    fontSize: 15,
    color: '#555',
    lineHeight: 22,
    marginBottom: 12,
  },
  overviewGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  overviewItem: {
    flex: 1,
    padding: 12,
    borderRadius: 12,
    backgroundColor: '#f8f9fb',
    marginRight: 8,
  },
  overviewLabel: {
    fontSize: 13,
    color: '#777',
  },
  overviewValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 4,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#2E7D32',
    padding: 14,
    borderRadius: 12,
    marginBottom: 12,
  },
  primaryButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    padding: 12,
    borderRadius: 12,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
  },
  link: {
    fontSize: 14,
    color: '#2E7D32',
    fontWeight: '600',
  },
  announcementRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  standingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  rankBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#E8F5E9',
    textAlign: 'center',
    textAlignVertical: 'center',
    color: '#2E7D32',
    fontWeight: '700',
    marginRight: 12,
  },
  playerName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
  },
  playerSubtext: {
    fontSize: 13,
    color: '#777',
  },
  participantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f6f6f6',
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#E0E0E0',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#555',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#555',
    marginTop: 8,
  },
  emptySubtitle: {
    fontSize: 13,
    color: '#888',
    marginTop: 4,
    textAlign: 'center',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  loadingText: {
    marginTop: 12,
    color: '#666',
  },
});

export default TournamentDetailScreen;

